/* eslint-disable no-nested-ternary */
import React, { memo, useState, useMemo, useCallback, useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { DateTime } from 'luxon';
import { toaster } from '@saleshandy/design-system';
import themedToaster, { Theme } from '../../../../shared/toaster';
import type { IProps } from './call-logs-container';
import CallLogsHeader from './components/call-logs-header/call-logs-header';
import CallLogsSubHeader from './components/call-logs-sub-header/call-logs-sub-header-container';
import CallLogsTable from './components/call-logs-table/call-logs-table';
import {
  useCallLogs,
  mapDialerListPayloadToCallLogs,
} from './hooks/use-call-logs';
import {
  getCallLogsProspectProfileSection,
  listAllCallLogs,
  listMissedCalls,
  listIncomingVoicemails,
} from '../../helpers/dialer.api';

import { RequestStatus } from '../../../../shared/enums/request-status';
import {
  CallLog,
  CallDirection,
  GetCallLogsListPayload,
} from '../../types/call-logs';
import { PaginationOptionsChangePayload } from '../../../../shared/design-system/ui/tanstack-table/types';
import './call-logs.scss';
import { executeOnRequestStatus } from '../../../../shared/utils/execute-on-request-status';
import CallLogsSearchEmptyState from './components/call-logs-tab-empty-state-templates/call-logs-search-empty-state';
import CallLogsEmptyState from './components/call-logs-tab-empty-state-templates/call-logs-empty-state';
import { CallLogsFiltersState } from './components/call-logs-filters/type';
import CallLogsFilters from './components/call-logs-filters';
import { listAllCallLogsPayload } from '../../types';
import ProspectDetails from '../../../prospect/components/prospect-list/components/prospect-details';
import CallLogsProspectDrawerHeader from './components/call-logs-prospect-drawer-header/call-logs-prospect-drawer-header-container';
import AddToDncModal from './components/add-to-dnc-modal/add-to-dnc-modal';
import { useProspectDetailsUrl } from '../../../../shared/hooks/use-prospect-details-url';
import { generateBaseUrl } from '../../../../shared/utils/prospect-details-url';
import { SortOrder } from '../../../../shared/enums/order';
import { DialerListSortType } from '../../enums/prospect';
import PurchaseDialerCard from './components/purchase-dialer-card';

const CallLogs: React.FC<IProps> = ({
  sendListAllCallLogsRequest,
  listMissedCallsRequest,
  listIncomingVoicemailsRequest,
  listAllCallLogsRequestStatus,
  listMissedCallsRequestStatus,
  listIncomingVoicemailsRequestStatus,
  setShowDialer,
  showDialer,
  currentCallManagementState,
  setActiveCallLogsTab,
  role,
  sendDeleteCallLogRequest,
  sendMarkUnseenMissedCallCountRequest,
  missedCallCount,
  deleteCallLogRequestStatus,
  resetDeleteCallLogRequest,
  sendMarkCallAsSeenRequest,
  activeCallLogsTab,
  callLogsFilters,
  userTimezone,
  setCallLogsFilters,
  resetCallLogsFilters,
  callLogsListCountsRequest,
  callLogsListCountsResponse,
  sendAssignCallOutcomeRequest,
  assignCallOutcomeRequestStatus,
  resetAssignCallOutcomeRequest,

  selectedProspectId,
  selectedProspectData,
  selectedProspectSequences,
  getSingleProspectDetailsStatus,
  getSingleProspectSequencesDetailsStatus,
  setSelectedProspectId,
  resetSelectedProspectId,
  resetSelectedProspectData,
  sendGetCallLogsProspectProfileSectionRequest,
  resetGetCallLogsProspectProfileSectionRequest,
  getCallLogsProspectProfileSectionRequestStatus,
  setSingleCallDetails,
  selectedCallbackProspect,

  createCallLogsNoteRequestStatus,
  resetCreateCallLogsNoteRequest,
  updateCallLogsNoteRequestStatus,
  resetUpdateCallLogsNoteRequest,
  createTaskRequestStatus,
  createTaskRequestMessage,
  getCallLogsProspectProfileSectionResponse,
  sendAddPhoneNumberRequest,
  resetAddPhoneNumberRequest,
  addPhoneNumberRequestStatus,
  clearProspectModalNavigationContext,
  myPhoneNumbersList,
  dialerSubscription,
  isDemoAccount,
  isDialerPlanPurchasedPreviously,
}) => {
  const location = useLocation();
  const { callLogId: callLogIdFromUrl, tab: tabFromUrlParam } = useParams<{
    callLogId?: string;
    tab?: string;
  }>();
  const history = useHistory();
  const { openProspectDetails } = useProspectDetailsUrl();

  /** When the current page has no row for the URL prospect, API result is stored here (does not touch Redux list). */
  const [fetchedRowForUrl, setFetchedRowForUrl] = useState<{
    key: string;
    row: CallLog | null;
  } | null>(null);

  /** Tracks URL param transitions so we only auto-close on browser back from a deep link, not for drawer opened on /dialer/call-logs without call log id in URL. */
  const [prevCallLogIdParam, setPrevCallLogIdParam] = useState<
    string | undefined
  >(callLogIdFromUrl);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{
    sortBy: DialerListSortType;
    order: SortOrder;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );
  const [showProspectDetailsModal, setShowProspectDetailsModal] = useState(
    false,
  );
  const [showDncModal, setShowDncModal] = useState(false);

  const [selectedCallLogRow, setSelectedCallLogRow] = useState<CallLog | null>(
    null,
  );

  // CRM list/kanban leaves prospectToNavigateFromModal.ids in Redux; dialer arrows then run CRM nav + API. Clear when dialer drawer opens.
  useEffect(() => {
    if (showProspectDetailsModal) {
      clearProspectModalNavigationContext();
    }
  }, [showProspectDetailsModal, clearProspectModalNavigationContext]);

  const isDialerFreePlan = useMemo(
    () => dialerSubscription?.planCode === 'dialer-free',
    [dialerSubscription?.planCode],
  );

  const tabType = useMemo(
    () => new URLSearchParams(location.search).get('type') || 'all',
    [location.search],
  );
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (callLogsFilters?.callerHost?.length > 0) count += 1;
    if (callLogsFilters?.source?.length > 0) count += 1;
    if (callLogsFilters?.callOutcome?.length > 0) count += 1;
    if (
      callLogsFilters.callDateStart?.trim?.() &&
      callLogsFilters.callDateEnd?.trim?.()
    ) {
      count += 1;
    }
    return count;
  }, [callLogsFilters]);

  const callLogsFiltersPayload = useMemo(
    () => ({
      ...(callLogsFilters?.callerHost?.length > 0
        ? { userIds: callLogsFilters.callerHost }
        : {}),
      ...(callLogsFilters?.callOutcome?.length > 0
        ? { callOutcomeIds: callLogsFilters.callOutcome }
        : {}),
      ...(callLogsFilters?.source?.length > 0
        ? { source: callLogsFilters.source }
        : {}),
      ...(callLogsFilters?.callDateStart
        ? {
            callDateStart: DateTime.fromISO(callLogsFilters.callDateStart)
              .setZone(userTimezone, { keepLocalTime: true })
              .toISO(),
          }
        : {}),
      ...(callLogsFilters?.callDateEnd
        ? {
            callDateEnd: DateTime.fromISO(callLogsFilters.callDateEnd)
              .setZone(userTimezone, { keepLocalTime: true })
              .toISO(),
          }
        : {}),
    }),
    [callLogsFilters, userTimezone],
  );

  const isLoading = useMemo(() => {
    if (tabType === 'missed') {
      return listMissedCallsRequestStatus === RequestStatus.Pending;
    }
    if (tabType === 'voicemails') {
      return listIncomingVoicemailsRequestStatus === RequestStatus.Pending;
    }
    return listAllCallLogsRequestStatus === RequestStatus.Pending;
  }, [
    tabType,
    listAllCallLogsRequestStatus,
    listMissedCallsRequestStatus,
    listIncomingVoicemailsRequestStatus,
  ]);

  const fetchCallLogsList = useCallback(
    (payload: listAllCallLogsPayload, tabTypeParam?: string) => {
      if (tabTypeParam === 'missed') {
        listMissedCallsRequest(payload);
      } else if (tabTypeParam === 'voicemails') {
        listIncomingVoicemailsRequest(payload);
      } else if (tabTypeParam === 'all') {
        sendListAllCallLogsRequest(payload);
      }
    },
    [
      listMissedCallsRequest,
      listIncomingVoicemailsRequest,
      sendListAllCallLogsRequest,
    ],
  );

  const handleCallLogsListRequest = useCallback(
    (payload?: listAllCallLogsPayload, tabTypeParam?: string) => {
      fetchCallLogsList?.(
        {
          pageNumber: payload?.pageNumber ?? page,
          pageSize: payload?.pageSize ?? limit,
          searchText: payload?.searchText ?? search,
          ...(payload?.userIds?.length > 0
            ? { userIds: payload?.userIds ?? callLogsFilters?.callerHost }
            : {}),
          ...(payload?.callOutcomeIds?.length > 0
            ? {
                callOutcomeIds:
                  payload?.callOutcomeIds ?? callLogsFilters?.callOutcome,
              }
            : {}),
          ...(payload?.source?.length > 0
            ? { source: payload?.source ?? callLogsFilters?.source }
            : {}),
          ...(payload?.callDateStart
            ? {
                callDateStart:
                  payload?.callDateStart ?? callLogsFilters?.callDateStart,
              }
            : {}),
          ...(payload?.callDateEnd
            ? {
                callDateEnd:
                  payload?.callDateEnd ?? callLogsFilters?.callDateEnd,
              }
            : {}),
          // eslint-disable-next-line no-nested-ternary
          ...(payload?.sortBy
            ? { sortBy: payload.sortBy, sortOrder: payload.sortOrder }
            : sort
            ? { sortBy: sort.sortBy, sortOrder: sort.order }
            : {}),
        },
        tabTypeParam,
      );
    },
    [fetchCallLogsList, callLogsFilters, search, limit, page, sort],
  );

  const handleSortChange = useCallback(
    (sortBy: string, order: SortOrder) => {
      const nextSort = { sortBy: sortBy as DialerListSortType, order };
      setSort(nextSort);
      setPage(1);
      handleCallLogsListRequest(
        {
          pageNumber: 1,
          pageSize: limit,
          searchText: search,
          ...callLogsFiltersPayload,
          sortBy: nextSort.sortBy,
          sortOrder: nextSort.order,
        },
        tabType,
      );
    },
    [limit, search, callLogsFiltersPayload, handleCallLogsListRequest, tabType],
  );

  const { callLogs, total } = useCallLogs({
    tabType: tabType as GetCallLogsListPayload['type'],
  });

  /** Call log row id (`CallLog.id`) from the URL — not prospect id. */
  const urlCallLogId = useMemo(() => {
    const n = callLogIdFromUrl ? Number(callLogIdFromUrl) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [callLogIdFromUrl]);

  const urlKey = useMemo(
    () =>
      urlCallLogId != null && callLogIdFromUrl
        ? `${callLogIdFromUrl}:${tabFromUrlParam ?? ''}`
        : null,
    [urlCallLogId, callLogIdFromUrl, tabFromUrlParam],
  );

  const rowOnPageForUrlCallLog = useMemo(() => {
    if (urlCallLogId == null) return null;
    return callLogs?.find((c) => c?.id === urlCallLogId) ?? null;
  }, [urlCallLogId, callLogs]);

  const rowResolvedForUrl = useMemo(() => {
    if (urlCallLogId == null) return null;
    if (rowOnPageForUrlCallLog) return rowOnPageForUrlCallLog;
    if (fetchedRowForUrl?.key === urlKey) return fetchedRowForUrl?.row;
    return null;
  }, [urlCallLogId, rowOnPageForUrlCallLog, fetchedRowForUrl, urlKey]);

  // Drawer keeps a copy of the row in local state; Redux updates the list when call outcome
  // is assigned — re-sync so the detail dropdown shows the new selection without a refresh.
  useEffect(() => {
    setSelectedCallLogRow((prev) => {
      if (!prev?.id || !callLogs?.length) return prev;
      const updated = callLogs.find((log) => log.id === prev.id);
      if (!updated) return prev;
      const a = prev.callOutcome;
      const b = updated.callOutcome;
      const same =
        a?.id === b?.id &&
        a?.name === b?.name &&
        a?.sentiment === b?.sentiment &&
        Boolean(a?.isDefault) === Boolean(b?.isDefault);
      return same ? prev : updated;
    });
  }, [callLogs]);

  useEffect(() => {
    if (selectedCallLogRow) {
      setSingleCallDetails(selectedCallLogRow);
    }
  }, [selectedCallLogRow, setSingleCallDetails]);

  const handlePaginationChange = useCallback(
    (payload: PaginationOptionsChangePayload) => {
      // Keep UI pagination in sync with table + fetch data
      if (payload.limit !== limit) {
        setLimit(payload.limit);
        setPage(1);
        handleCallLogsListRequest(
          {
            pageNumber: 1,
            pageSize: payload.limit,
            searchText: search,
            ...callLogsFiltersPayload,
          },
          tabType,
        );
        return;
      }

      if (payload.page !== page) {
        const nextLimit = limit;
        const maxPage =
          total <= 0 ? 1 : Math.max(1, Math.ceil(total / nextLimit));
        const nextPage = Math.min(Math.max(1, payload.page), maxPage);
        setPage(nextPage);
        handleCallLogsListRequest(
          {
            pageNumber: nextPage,
            pageSize: nextLimit,
            searchText: search,
            ...callLogsFiltersPayload,
          },
          tabType,
        );
      }
    },
    [
      page,
      limit,
      total,
      handleCallLogsListRequest,
      callLogsFiltersPayload,
      search,
      tabType,
    ],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      if (value === search) {
        return;
      }
      setSearch(value);
      setPage(1);
      handleCallLogsListRequest(
        {
          pageNumber: 1,
          pageSize: limit,
          searchText: value,
          ...callLogsFiltersPayload,
        },
        tabType,
      );
    },
    [search, limit, handleCallLogsListRequest, tabType, callLogsFiltersPayload],
  );

  const handleFilterClick = useCallback(() => {
    setShowFilters(true);
  }, []);

  const handleFiltersApply = useCallback(
    (nextFilters: CallLogsFiltersState) => {
      setCallLogsFilters(nextFilters);
      setPage(1);
      handleCallLogsListRequest(
        {
          pageNumber: 1,
          pageSize: limit,
          searchText: search,
          source: nextFilters.source,
          userIds: nextFilters.callerHost,
          callOutcomeIds: nextFilters.callOutcome,
          callDateStart: nextFilters.callDateStart
            ? DateTime.fromISO(nextFilters.callDateStart)
                .setZone(userTimezone, { keepLocalTime: true })
                .toISO()
            : undefined,
          callDateEnd: nextFilters.callDateEnd
            ? DateTime.fromISO(nextFilters.callDateEnd)
                .setZone(userTimezone, { keepLocalTime: true })
                .toISO()
            : undefined,
        },
        tabType,
      );
    },
    [
      search,
      limit,
      handleCallLogsListRequest,
      setCallLogsFilters,
      tabType,
      userTimezone,
    ],
  );

  const handleFiltersClear = useCallback(() => {
    resetCallLogsFilters();
    setPage(1);
    handleCallLogsListRequest(
      {
        pageNumber: 1,
        pageSize: limit,
        searchText: search,
        userIds: [],
        callOutcomeIds: [],
        source: [],
        callDateStart: '',
        callDateEnd: '',
      },
      tabType,
    );
    setShowFilters(false);
  }, [search, limit, resetCallLogsFilters, tabType, handleCallLogsListRequest]);

  // Mark all missed calls as seen
  const handleMarkCallAsSeen = useCallback(() => {
    // Empty array to mark all missed calls as seen
    sendMarkCallAsSeenRequest([]);
  }, []);

  const handleMarkSingleCallAsSeen = useCallback(
    (callId: number) => {
      sendMarkCallAsSeenRequest([callId]);
    },
    [sendMarkCallAsSeenRequest],
  );

  const hideProspectDetailsModal = useCallback(() => {
    resetSelectedProspectId();
    resetSelectedProspectData();
    setSelectedCallLogRow(null);
    setShowProspectDetailsModal(false);
    setFetchedRowForUrl(null);

    const params = new URLSearchParams(location.search);
    params.delete('callId');
    const query = params.toString();
    history.replace({
      pathname: generateBaseUrl(location.pathname),
      search: query ? `?${query}` : '',
      hash: location.hash,
    });
  }, [
    resetSelectedProspectId,
    resetSelectedProspectData,
    history,
    location.pathname,
    location.search,
    location.hash,
  ]);

  useEffect(() => {
    if (selectedCallbackProspect?.length > 0) {
      hideProspectDetailsModal();
    }
  }, [selectedCallbackProspect, hideProspectDetailsModal]);

  const handleProspectNameClick = useCallback(
    (ctx: { columnId: number; row: CallLog }) => {
      setSelectedCallLogRow(ctx.row);
      const prospectId = ctx?.row?.prospect?.id;
      const isAccessible = ctx?.row?.isProspectAccessible !== false;

      if (prospectId && isAccessible) {
        setSelectedProspectId(prospectId);
        openProspectDetails(ctx?.row?.id, 'activity');
        // Profile API is invoked once by the URL-sync effect below after the path updates.
        // Calling it here too caused a duplicate request (click + effect).
        // sendGetCallLogsProspectProfileSectionRequest(ctx.row.id);
      } else {
        resetSelectedProspectId();
        openProspectDetails(ctx?.row?.id, 'activity');
        // sendGetCallLogsProspectProfileSectionRequest(ctx?.row?.id);
      }
      setShowProspectDetailsModal(true);

      if (ctx.row.callDirection === CallDirection.Missed && !ctx.row.isSeen) {
        sendMarkCallAsSeenRequest([ctx.row.id]);
      }
    },
    [
      setSelectedProspectId,
      resetSelectedProspectId,
      openProspectDetails,
      sendGetCallLogsProspectProfileSectionRequest,
      getCallLogsProspectProfileSectionResponse?.dialerPhoneNumber?.id,
    ],
  );

  // Clear deep-link fetch cache when leaving call-log URLs or when the list already contains the row.
  useEffect(() => {
    if (urlKey == null) {
      setFetchedRowForUrl(null);
      return;
    }
    if (rowOnPageForUrlCallLog) {
      setFetchedRowForUrl(null);
    }
  }, [urlKey, rowOnPageForUrlCallLog]);

  // Fetch a call row when deep-linked call id is not on the current page (no Redux overwrite).
  useEffect(() => {
    if (urlKey == null || urlCallLogId == null) {
      return;
    }
    if (rowOnPageForUrlCallLog) {
      return;
    }
    if (isLoading) {
      return;
    }
    if (fetchedRowForUrl?.key === urlKey) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        const res: any = await getCallLogsProspectProfileSection(urlCallLogId);
        const profile = res?.payload ?? res;
        const prospectIdFromProfile = profile?.prospect?.id;
        if (!prospectIdFromProfile) {
          if (!cancelled) {
            setFetchedRowForUrl({ key: urlKey, row: null });
          }
          return;
        }

        const minimalPayload: listAllCallLogsPayload = {
          pageNumber: 1,
          pageSize: 100,
          prospectIds: [prospectIdFromProfile],
          searchText: '',
        };

        let listRes = await listAllCallLogs(minimalPayload);
        let { callLogs: fetched } = mapDialerListPayloadToCallLogs(
          listRes?.payload,
          'all',
        );
        if (fetched.length === 0) {
          listRes = await listMissedCalls(minimalPayload);
          fetched = mapDialerListPayloadToCallLogs(listRes?.payload, 'missed')
            .callLogs;
        }
        if (fetched.length === 0) {
          listRes = await listIncomingVoicemails(minimalPayload);
          fetched = mapDialerListPayloadToCallLogs(
            listRes?.payload,
            'voicemails',
          ).callLogs;
        }
        const row =
          fetched.find((c) => c.id === urlCallLogId) ?? fetched[0] ?? null;
        if (!cancelled) {
          setFetchedRowForUrl({ key: urlKey, row });
        }
      } catch {
        if (!cancelled) {
          setFetchedRowForUrl({ key: urlKey, row: null });
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    urlKey,
    urlCallLogId,
    rowOnPageForUrlCallLog,
    isLoading,
    fetchedRowForUrl?.key,
  ]);

  // Open / update drawer from deep link when we have a call row (current page or fetch).
  useEffect(() => {
    if (urlCallLogId == null) {
      return;
    }
    const row = rowResolvedForUrl;
    if (!row?.id) {
      return;
    }
    const prospectId = row.prospect?.id;
    const isAccessible = row.isProspectAccessible !== false;
    if (prospectId && isAccessible) {
      setSelectedProspectId(prospectId);
    } else {
      resetSelectedProspectId();
    }
    setSelectedCallLogRow(row);
    sendGetCallLogsProspectProfileSectionRequest(row.id);
    setShowProspectDetailsModal(true);
  }, [
    urlCallLogId,
    rowResolvedForUrl?.id,
    rowResolvedForUrl?.prospect?.id,
    setSelectedProspectId,
    resetSelectedProspectId,
  ]);

  // Browser back: URL had :callLogId and now does not — close drawer.
  useEffect(() => {
    const next = callLogIdFromUrl;
    const prev = prevCallLogIdParam;

    const hadCallLogInUrl = prev != null && prev !== '' && Number(prev) > 0;
    const hasCallLogInUrl = next != null && next !== '' && Number(next) > 0;

    if (hadCallLogInUrl && !hasCallLogInUrl && showProspectDetailsModal) {
      resetSelectedProspectId();
      resetSelectedProspectData();
      setSelectedCallLogRow(null);
      setShowProspectDetailsModal(false);
      setFetchedRowForUrl(null);
    }

    setPrevCallLogIdParam(next);
  }, [
    callLogIdFromUrl,
    prevCallLogIdParam,
    showProspectDetailsModal,
    resetSelectedProspectId,
    resetSelectedProspectData,
  ]);

  // Fetch call logs list on mount only
  useEffect(() => {
    callLogsListCountsRequest();
  }, []);

  // Fetch on mount when navigating from voicemail-templates/reports to call-logs tabs
  useEffect(() => {
    const payload: listAllCallLogsPayload = {
      pageNumber: page,
      pageSize: limit || 25,
      searchText: search || '',
      ...callLogsFiltersPayload,
    };
    if (tabType === 'missed') {
      listMissedCallsRequest(payload);
    } else if (tabType === 'voicemails') {
      listIncomingVoicemailsRequest(payload);
    } else {
      sendListAllCallLogsRequest(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch only on mount with current filters
  }, []);

  // missed call count api request
  useEffect(() => {
    if (!missedCallCount || missedCallCount === 0) {
      sendMarkUnseenMissedCallCountRequest();
    }
  }, []);

  // delete call log api request
  useEffect(() => {
    executeOnRequestStatus({
      status: deleteCallLogRequestStatus?.status,
      onSuccess: () => {
        // missed call count
        sendMarkUnseenMissedCallCountRequest();
        toaster.success(deleteCallLogRequestStatus?.message);
        // If on last page with only one item, go to previous page so list is not empty
        const hadSingleItemOnPage =
          page > 1 && total === (page - 1) * limit + 1;
        if (hadSingleItemOnPage) {
          setPage(page - 1);
          handleCallLogsListRequest(
            {
              pageNumber: page - 1,
              pageSize: limit,
              searchText: search,
              ...callLogsFiltersPayload,
            },
            tabType,
          );
        } else {
          handleCallLogsListRequest(
            {
              pageNumber: page,
              pageSize: limit,
              searchText: search,
              ...callLogsFiltersPayload,
            },
            tabType,
          );
        }
        callLogsListCountsRequest();
        resetDeleteCallLogRequest();
      },
      onFailed: () => {
        resetDeleteCallLogRequest();
      },
    });
  }, [
    deleteCallLogRequestStatus?.status,
    page,
    limit,
    total,
    search,
    tabType,
    handleCallLogsListRequest,
    callLogsListCountsRequest,
  ]);

  // Refresh call logs list and counts when a call ends (only on 'all' tab)
  useEffect(() => {
    const handleCallEnded = () => {
      if (tabType === 'all') {
        setPage(1);
        setLimit(25);
        setSearch('');
        resetCallLogsFilters();
        handleCallLogsListRequest(
          {
            pageNumber: 1,
            pageSize: 25,
            searchText: '',
          },
          tabType,
        );
        callLogsListCountsRequest();
      }
    };

    window.addEventListener('sh:call-ended', handleCallEnded);
    return () => {
      window.removeEventListener('sh:call-ended', handleCallEnded);
    };
  }, [
    tabType,
    page,
    limit,
    search,
    callLogsFiltersPayload,
    handleCallLogsListRequest,
    callLogsListCountsRequest,
  ]);

  // Assign call outcome api request
  useEffect(() => {
    executeOnRequestStatus({
      status: assignCallOutcomeRequestStatus?.status,
      onSuccess: () => {
        toaster.success(assignCallOutcomeRequestStatus?.message);
        resetAssignCallOutcomeRequest();
      },
      onFailed: () => {
        toaster.error(assignCallOutcomeRequestStatus?.message);
        resetAssignCallOutcomeRequest();
      },
    });
  }, [assignCallOutcomeRequestStatus?.status]);

  // Get call logs prospect profile section api request
  useEffect(() => {
    executeOnRequestStatus({
      status: getCallLogsProspectProfileSectionRequestStatus,
      onSuccess: () => {
        resetGetCallLogsProspectProfileSectionRequest();
      },
      onFailed: () => {
        resetGetCallLogsProspectProfileSectionRequest();
      },
    });
  }, [
    getCallLogsProspectProfileSectionRequestStatus,
    getCallLogsProspectProfileSectionResponse?.dialerPhoneNumber?.id,
  ]);

  // reset Note Request Status
  useEffect(() => {
    executeOnRequestStatus({
      status: createCallLogsNoteRequestStatus,
      onSuccess: () => {
        toaster.success('Note saved successfully');
        resetCreateCallLogsNoteRequest();
      },
      onFailed: () => {
        resetCreateCallLogsNoteRequest();
      },
    });
  }, [createCallLogsNoteRequestStatus, resetCreateCallLogsNoteRequest]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updateCallLogsNoteRequestStatus,
      onSuccess: () => {
        toaster.success('Note updated successfully');
        resetUpdateCallLogsNoteRequest();
      },
      onFailed: () => {
        resetUpdateCallLogsNoteRequest();
      },
    });
  }, [updateCallLogsNoteRequestStatus, resetUpdateCallLogsNoteRequest]);

  useEffect(() => {
    executeOnRequestStatus({
      status: createTaskRequestStatus,
      onSuccess: () => {
        toaster.success(createTaskRequestMessage);
      },
    });
  }, [createTaskRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: addPhoneNumberRequestStatus,
      onSuccess: () => {
        themedToaster.success('Phone number added to DNC list successfully', {
          theme: Theme.New,
        });
        setShowDncModal(false);
        resetAddPhoneNumberRequest();
      },
    });
  }, [addPhoneNumberRequestStatus]);

  return (
    <div className="call-logs">
      {isDialerFreePlan && !isDialerPlanPurchasedPreviously ? (
        <div className="purchase-dialer-card-container">
          <PurchaseDialerCard />
        </div>
      ) : (
        <>
          {' '}
          <CallLogsHeader
            resetCallLogsFilters={resetCallLogsFilters}
            onMakeCallClick={setShowDialer}
            showDialer={showDialer}
            setActiveCallLogsTab={setActiveCallLogsTab}
            search={search}
            sendMarkUnseenMissedCallCountRequest={
              sendMarkUnseenMissedCallCountRequest
            }
            handleCallLogsListRequest={handleCallLogsListRequest}
            callLogsListCountsResponse={callLogsListCountsResponse}
            myPhoneNumbersList={myPhoneNumbersList}
          />
          <CallLogsSubHeader
            search={search}
            onSearchChange={handleSearchChange}
            onFilterClick={handleFilterClick}
            activeFilterCount={activeFilterCount}
          />
          {!isLoading && total === 0 ? (
            <div className="call-logs__empty-section">
              {search ? (
                <CallLogsSearchEmptyState />
              ) : activeFilterCount > 0 ? (
                <CallLogsSearchEmptyState filterApplied={true} />
              ) : (
                <CallLogsEmptyState />
              )}
            </div>
          ) : (
            <CallLogsTable
              data={callLogs ?? []}
              isLoading={isLoading}
              page={page}
              limit={limit}
              totalRows={total}
              onPaginationChange={handlePaginationChange}
              tabType={tabType as GetCallLogsListPayload['type']}
              currentCallManagementState={currentCallManagementState}
              role={role}
              sendDeleteCallLogRequest={sendDeleteCallLogRequest}
              missedCallCount={missedCallCount}
              handleMarkCallAsSeen={handleMarkCallAsSeen}
              activeCallLogsTab={activeCallLogsTab}
              sendAssignCallOutcomeRequest={sendAssignCallOutcomeRequest}
              setSelectedTemplateId={setSelectedTemplateId}
              selectedTemplateId={selectedTemplateId}
              onProspectNameClick={handleProspectNameClick}
              onMarkSingleCallAsSeen={handleMarkSingleCallAsSeen}
              sort={sort}
              onSort={handleSortChange}
              isDemoAccount={isDemoAccount}
            />
          )}
          <CallLogsFilters
            show={showFilters}
            onClose={() => setShowFilters(false)}
            initialFilters={callLogsFilters}
            onApply={handleFiltersApply}
            onClearAll={handleFiltersClear}
            activeTab={activeCallLogsTab}
          />
        </>
      )}

      {showProspectDetailsModal && selectedCallLogRow && (
        <ProspectDetails
          show={showProspectDetailsModal}
          onHide={hideProspectDetailsModal}
          setSelectedProspectId={setSelectedProspectId}
          selectedProspectId={selectedProspectId ?? 0}
          resetSelectedProspectId={resetSelectedProspectId}
          selectedProspectData={selectedProspectData}
          selectedProspectSequences={selectedProspectSequences}
          getSingleProspectDetailsStatus={getSingleProspectDetailsStatus}
          getSingleProspectSequencesDetailsStatus={
            getSingleProspectSequencesDetailsStatus
          }
          isTagsFilterApplied={false}
          defaultTab="activity"
          callLogsVariant
          callLogRow={selectedCallLogRow}
          callLogsProfileHeader={
            <CallLogsProspectDrawerHeader
              row={selectedCallLogRow}
              selectedProspectId={selectedProspectId}
              onDoNotContactClick={() => setShowDncModal(true)}
            />
          }
          prospectDetail="Call Logs"
        />
      )}
      {showDncModal && (
        <AddToDncModal
          show={showDncModal}
          onClose={() => setShowDncModal(false)}
          phoneNumber={`${
            selectedCallLogRow?.prospectPhone?.number ??
            selectedCallLogRow?.prospectPhone?.phoneNumber ??
            ''
          }`}
          isSubmitting={addPhoneNumberRequestStatus === RequestStatus.Pending}
          onAddToDnc={(dncListId) => {
            const phone = selectedCallLogRow?.prospectPhone;
            if (!phone) return;
            const rawNumber = phone.number ?? '';
            sendAddPhoneNumberRequest({
              dncListId,
              phoneNumber: rawNumber,
              updateOutcome: false,
            });
          }}
        />
      )}
    </div>
  );
};

export default memo(CallLogs);
